import { computed, type Ref, toValue, type WritableComputedRef } from 'vue'
import { clamp, type RefOrGetter } from '@/helpers'
import type { InputRangeModel } from '@/components/ui/inputs/range/input-range-two-knobbed-props.ts'

type InputRangeModels = {
    min: WritableComputedRef<number, number>
    max: WritableComputedRef<number, number>
}

export function useInputRangeModels(
    model: RefOrGetter<InputRangeModel>,
    min: Ref<number>,
    max: Ref<number>,
    update: (model: InputRangeModel) => void,
    restrictInput?: boolean // clamps min to current max and vice versa - more intuitive for text input fields
): InputRangeModels {
    return {
        min: computed({
            get() {
                return clamp(
                    toValue(model)[0] ?? min.value,
                    min.value,
                    max.value
                )
            },
            set(val: number) {
                let result: InputRangeModel = [null, null]
                const maxVal = toValue(model)[1] ?? max.value

                if (restrictInput) {
                    result = [val, Math.max(val, maxVal)]
                } else {
                    result = sortModel([val, maxVal])
                }

                update(result)
            },
        }),
        max: computed({
            get() {
                return clamp(
                    toValue(model)[1] ?? max.value,
                    min.value,
                    max.value
                )
            },
            set(val: number) {
                let result: InputRangeModel = [null, null]
                const minVal = toValue(model)[0] ?? min.value

                if (restrictInput) {
                    result = [Math.min(val, minVal), val]
                } else {
                    result = sortModel([minVal, val])
                }

                update(result)
            },
        }),
    }
}

function sortModel(model: [number, number]): [number, number] {
    return model.sort((a, b) => {
        return a - b
    })
}
